import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Typography,
} from '@mui/material';
import Modal from './Modal';
import {
	fetchUpgradeSeatSelection,
	confirmUpgradeSeatSelection,
} from '../../Shared/Api';
import { EXTRA_SEAT_MONTHLY, PLAN_PRICE, PRO_INCLUDED_SEATS } from '../../Shared/pricing';

const UpgradeSeatSelectionModal = ({
	open,
	onClose,
	onSuccess,
	enqueueSnackbar,
}) => {
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [selection, setSelection] = useState(null);
	const [selectedIds, setSelectedIds] = useState([]);

	useEffect(() => {
		if (!open) {
			return;
		}
		setLoading(true);
		fetchUpgradeSeatSelection()
			.then((response) => {
				setSelection(response);
				setSelectedIds(response?.selected_by_default || []);
			})
			.catch((error) => {
				enqueueSnackbar?.(error.message || 'Failed to load seat selection.', {
					variant: 'error',
				});
				onClose?.();
			})
			.finally(() => setLoading(false));
	}, [open, enqueueSnackbar, onClose]);

	const heldUsers = useMemo(() => selection?.held_users || [], [selection]);
	const deletedEmails = useMemo(
		() =>
			heldUsers
				.filter((user) => !selectedIds.includes(user.id))
				.map((user) => user.email),
		[heldUsers, selectedIds],
	);

	const toggleUser = (userId) => {
		setSelectedIds((prev) =>
			prev.includes(userId)
				? prev.filter((id) => id !== userId)
				: [...prev, userId],
		);
	};

	const handleConfirm = async () => {
		setSubmitting(true);
		try {
			await confirmUpgradeSeatSelection(selectedIds);
			enqueueSnackbar?.('Subscription upgraded and seats updated.', {
				variant: 'success',
			});
			onSuccess?.();
			onClose?.();
		} catch (error) {
			enqueueSnackbar?.(error.message || 'Upgrade failed.', {
				variant: 'error',
			});
		} finally {
			setSubmitting(false);
		}
	};

	const preview = selection?.cost_preview;
	const proBase = preview?.pro_base_monthly_usd ?? PLAN_PRICE.pro;
	const extraSeat = preview?.extra_seat_monthly_usd ?? EXTRA_SEAT_MONTHLY;
	const includedSeats = preview?.included_seats ?? PRO_INCLUDED_SEATS;

	return (
		<Modal
			open={open}
			onClose={onClose}
			modalTitle="Choose seats for Pro"
			maxWidth="sm"
			useActionButtons
			actionButtons={
				<>
					<Button onClick={onClose} disabled={submitting}>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="primary"
						onClick={handleConfirm}
						disabled={loading || submitting}
					>
						{submitting ? 'Upgrading...' : 'Upgrade to Pro'}
					</Button>
				</>
			}
		>
			{loading ? (
				<Typography variant="body2" color="text.secondary">
					Loading seat options...
				</Typography>
			) : (
				<Box sx={{ mt: 1 }}>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						Pro includes up to {includedSeats} active seats for ${proBase}/mo. Each additional active
						seat is ${extraSeat}/mo. Select which previously deactivated accounts to
						restore on Pro.
					</Typography>

					<List dense>
						{heldUsers.map((user) => (
							<ListItem key={user.id} disablePadding>
								<FormControlLabel
									sx={{ width: '100%', mx: 0 }}
									control={
										<Checkbox
											checked={selectedIds.includes(user.id)}
											onChange={() => toggleUser(user.id)}
										/>
									}
									label={`${user.email} (${user.role})`}
								/>
							</ListItem>
						))}
					</List>

					{preview ? (
						<Box sx={{ mb: 2 }}>
							<Typography variant="subtitle2" gutterBottom>
								Cost summary
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Seats 1–{includedSeats} included in ${proBase}/mo
								{preview.target_billable_extra_seats > 0
									? ` · ${preview.target_billable_extra_seats} extra seat(s) at $${extraSeat}/mo each`
									: ''}
							</Typography>
							{preview.prorated_amount_cents != null ? (
								<Typography variant="body2" color="text.secondary">
									{preview.prorated_credit ? 'Prorated credit' : 'Due today'}: $
									{(Math.abs(preview.prorated_amount_cents) / 100).toFixed(2)}
								</Typography>
							) : null}
						</Box>
					) : null}

					<Alert severity="warning" sx={{ mb: 1 }}>
						Accounts you do not select will be permanently deleted and cannot be
						restored.
					</Alert>
					{deletedEmails.length > 0 ? (
						<List dense>
							{deletedEmails.map((email) => (
								<ListItem key={email} disablePadding>
									<ListItemIcon sx={{ minWidth: 28 }}>•</ListItemIcon>
									<ListItemText primary={email} />
								</ListItem>
							))}
						</List>
					) : (
						<Typography variant="body2" color="text.secondary">
							All accounts selected — none will be deleted.
						</Typography>
					)}
				</Box>
			)}
		</Modal>
	);
};

export default UpgradeSeatSelectionModal;
